/**
 * Logger utility for controlled logging based on environment
 * Replaces direct console.log usage throughout the app
 */

/* eslint-disable no-console */

const isDevelopment = process.env.NODE_ENV === 'development';
const logLevel =
  process.env.REACT_APP_LOG_LEVEL || (isDevelopment ? 'debug' : 'error');

const LOG_LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
};

const currentLogLevel = LOG_LEVELS[logLevel] ?? LOG_LEVELS.info;

class Logger {
  constructor(context = 'App') {
    this.context = context;
  }

  _shouldLog(level) {
    return LOG_LEVELS[level] <= currentLogLevel;
  }

  _formatMessage(level, message, ...args) {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}] [${this.context}]`;
    return [prefix, message, ...args];
  }

  error(message, ...args) {
    if (this._shouldLog('error')) {
      console.error(...this._formatMessage('error', message, ...args));
    }
  }

  warn(message, ...args) {
    if (this._shouldLog('warn')) {
      console.warn(...this._formatMessage('warn', message, ...args));
    }
  }

  info(message, ...args) {
    if (this._shouldLog('info')) {
      console.info(...this._formatMessage('info', message, ...args));
    }
  }

  debug(message, ...args) {
    if (this._shouldLog('debug')) {
      console.log(...this._formatMessage('debug', message, ...args));
    }
  }

  // Special method for logging in production (only errors)
  log(message, ...args) {
    if (isDevelopment) {
      this.debug(message, ...args);
    }
  }

  // Group logging for better organization
  group(label) {
    if (isDevelopment) {
      console.group(label);
    }
  }

  groupEnd() {
    if (isDevelopment) {
      console.groupEnd();
    }
  }

  // Table logging for structured data
  table(data) {
    if (isDevelopment && this._shouldLog('debug')) {
      console.table(data);
    }
  }

  // Performance logging
  time(label) {
    if (isDevelopment) {
      console.time(label);
    }
  }

  timeEnd(label) {
    if (isDevelopment) {
      console.timeEnd(label);
    }
  }
}

// Factory function to create loggers with specific contexts
export const createLogger = context => new Logger(context);

// Default logger instance
const logger = new Logger();

export default logger;
